import { Component, HostListener, OnInit } from '@angular/core';
import { DeviceCategory } from '../../../model/device-category';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceCategoryPersistRequest } from '../../../model/requests/device-category-persist-request';
import { DeviceCategoriesService } from '../../../services/device-categories.service';
import { TenantsService } from '../../../services/tenants.service';
import { Tenant } from '../../../model/tenant';
import { TranslocoService } from '@ngneat/transloco';
import { noSpecialCharacters } from '../../../shared/custom-validators';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AuthGuard } from '../../../common/auth-guard.service';
import { CommonMessagesService } from "../../../services/common-messages.service";

@Component({
  selector: 'app-device-categories-form',
  templateUrl: './device-categories-form.component.html',
  styleUrls: ['./device-categories-form.component.scss']
})
export class DeviceCategoriesFormComponent implements OnInit, ComponentCanDeactivate {

  public category: DeviceCategory;

  public tenants: Tenant[];

  public categoryForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: {tenantUuid: string, oldName: string};

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private deviceCategoriesService: DeviceCategoriesService,
              private tenantsService: TenantsService,
              private commonMessagesService: CommonMessagesService) {}

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path);
      }
    });
  }

  loadCreationForm(): void {
    this.category = new DeviceCategory();
    if (!this.authGuard.superAdminRequired()) {
      this.category.tenant = this.tenants[0];
    }
    this.createForm(this.category);
  }

  loadEditionForm(tenantUuid, name): void {
    this.tenantsService.getTenantDeviceCategory(tenantUuid, name).toPromise().then(response => {
      this.category = response.deviceCategory;
      this.tenants = [this.category.tenant];
      this.editInfo = {tenantUuid: this.category.tenant.uuid, oldName: this.category.name};
      this.createForm(this.category);
    });
  }

  private createForm(category: DeviceCategory): void {
    this.categoryForm = this.formBuilder.group({
      name: [category.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      description: [category.description, [Validators.maxLength(255)]],
      tenant: [category.tenant, [Validators.required]]});
    this.categoryForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.categoryForm.get('tenant').disable();
    }

    if (this.mode === 'edit') {
      this.categoryForm.get('tenant').disable();
    }
  }

  persistCategoryData(): void {
    if (!this.categoryForm.valid) { return; }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.deviceCategoriesService.createDeviceCategory(persistData).toPromise();
    } else {
      promise = this.deviceCategoriesService.editDeviceCategory(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/device-categories`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): DeviceCategoryPersistRequest {
    const formValue = this.categoryForm.getRawValue();
    return {
      name: formValue.name,
      description: formValue.description,
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      oldName: this.mode === 'edit' ? this.editInfo.oldName : null
    };
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.categoryForm.untouched;
  }

}
